import { useMediaQuery } from '@mui/material';
import useTheme from '@/theme/useTheme';

/**
 * A comprehensive hook that provides responsive UI utilities
 * including font sizes, spacing, and breakpoint information
 */
const useResponsiveUI = () => {
  const { theme } = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isTablet = useMediaQuery(theme.breakpoints.up('sm'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  /**
   * Get responsive font size based on screen size
   * @param mobileFontSize - Font size for mobile (default: 12)
   * @param desktopFontSize - Font size for desktop (default: 14)
   * @returns Font size with 'px' unit
   */
  const getFontSize = (mobileFontSize = 12, desktopFontSize = 14) => {
    return isDesktop ? `${desktopFontSize}px` : `${mobileFontSize}px`;
  };

  /**
   * Get responsive spacing based on screen size
   * @param mobileSpacing - Spacing for mobile (default: 2)
   * @param desktopSpacing - Spacing for desktop (default: 3)
   * @returns Spacing value to use in MUI sx prop
   */
  const getSpacing = (mobileSpacing = 2, desktopSpacing = 3) => {
    return isDesktop ? desktopSpacing : mobileSpacing;
  };

  /**
   * Get responsive padding based on screen size
   * @param mobilePadding - Padding for mobile (can be object with top/right/bottom/left)
   * @param desktopPadding - Padding for desktop (can be object with top/right/bottom/left)
   * @returns Padding object to use in MUI sx prop
   */
  const getPadding = (mobilePadding: number | object, desktopPadding: number | object) => {
    return isDesktop ? desktopPadding : mobilePadding;
  };

  return {
    // Screen size helpers
    isDesktop,
    isTablet,
    isMobile,
    
    // Styling helpers
    getFontSize,
    getSpacing,
    getPadding,
    
    // Theme access
    theme
  };
};

export default useResponsiveUI; 